import React from 'react';
import { useTranslation } from 'react-i18next';
import { SentenceAnnotation } from '../../types/chat';
import MarkdownRenderer, { isMarkdownContent } from '../common/MarkdownRenderer';
import { buildHighlightSegments } from './sentenceHighlight';
import { cn } from '../../lib/utils';
import { expandLiteralValues } from '../../utils/literalValues';

/**
 * Tinted rather than solid so the text keeps the body colour and its contrast
 * in both themes; the literals this replaced were fixed at MUI's light palette.
 */
const HIGHLIGHT_CLASS: Record<string, string> = {
  supported: 'bg-success/15',
  unsupported: 'bg-warning/20',
};

interface HighlightedContentProps {
  content: string;
  annotations?: SentenceAnnotation[];
  className?: string;
  literalValues?: string[];
}

/** An answer with each verified sentence tinted by whether a source backs it. */
const HighlightedContent: React.FC<HighlightedContentProps> = ({
  content,
  annotations,
  className,
  literalValues,
}) => {
  const { t } = useTranslation();

  if (!annotations || annotations.length === 0) {
    return isMarkdownContent(content) ? (
      <MarkdownRenderer content={content} variant="body2" literalValues={literalValues} />
    ) : (
      <p className={cn('text-sm', className)}>{expandLiteralValues(content, literalValues)}</p>
    );
  }

  return (
    <div className={cn('whitespace-pre-wrap text-sm leading-[1.8]', className)}>
      {/* Segments render as text, so expanding first also restores the annotated sentences. */}
      {buildHighlightSegments(expandLiteralValues(content, literalValues), annotations).map((segment, index) =>
        segment.status ? (
          <span
            key={index}
            className={cn(
              'rounded px-[3px] py-[2px] transition-colors',
              HIGHLIGHT_CLASS[segment.status],
            )}
            title={t(
              segment.status === 'supported'
                ? 'chat.verificationSupportedTitle'
                : 'chat.verificationUnsupportedTitle',
            )}
          >
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </div>
  );
};

export default HighlightedContent;
